// The first linked browser becomes the account-key holder (D41): choose a recovery
// passphrase, download the encrypted recovery file (as often as needed until "finish"), then the key is kept
// non-extractable here, its public half registered, and this browser signs the first list.
import { DownloadSimple } from "@phosphor-icons/react";
import { type FormEvent, useState } from "react";

import { useT } from "../i18n";
import type { Proto } from "../proto/index";
import { PanelFingerprint, PanelInput, PanelNote, PanelPrimary, PanelSecondary } from "./AccountPanel";
import { downloadRecoveryFile } from "./download";
import { groupFingerprint } from "./key";
import { MIN_PASSPHRASE_LENGTH, passphraseProblem } from "./recovery";
import { finishAccountKey, type PreparedAccountKey, prepareAccountKey } from "./setup";

interface Props {
  proto: Proto;
  deviceId: string;
  onDone: () => void;
  onError: (message: string) => void;
}

export function CreateAccountKey({ proto, deviceId, onDone, onError }: Props) {
  const t = useT().account.create;
  const [passphrase, setPassphrase] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [prepared, setPrepared] = useState<PreparedAccountKey>();
  const [downloaded, setDownloaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const problem = passphraseProblem(passphrase, confirmation);

  async function run(step: () => Promise<void>) {
    setBusy(true);
    try {
      await step();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function onPrepare(e: FormEvent) {
    e.preventDefault();
    if (problem) return;
    void run(async () => {
      setPrepared(await prepareAccountKey(proto, passphrase));
      setPassphrase("");
      setConfirmation("");
    });
  }

  function onDownload() {
    if (!prepared) return;
    // Repeatable until "finish": a phone may drop the first download behind a prompt.
    downloadRecoveryFile(prepared.recoveryFile);
    setDownloaded(true);
  }

  function onFinish() {
    if (!prepared) return;
    // The extractable copy is wiped as soon as the key is stored. Whatever happens next,
    // the status is re-read and the file is not offered again: a key stored before a later
    // step failed shows up as held, and "Approve devices" finishes the list.
    void run(() => finishAccountKey(proto, prepared, deviceId).then(() => undefined)).finally(() => {
      setPrepared(undefined);
      onDone();
    });
  }

  if (prepared) {
    return (
      <div aria-label={t.saveLabel} role="group">
        <PanelFingerprint value={groupFingerprint(prepared.fingerprint)} testId="account-fingerprint" />
        <PanelNote>
          <span className="font-semibold text-white">{t.saveStrong}</span>
          {t.saveNote}
        </PanelNote>
        <div className="mt-5 flex flex-wrap gap-2.5">
          <PanelPrimary disabled={busy} onClick={onDownload}>
            <DownloadSimple weight="bold" className="h-4 w-4" /> {downloaded ? t.downloadAgain : t.download}
          </PanelPrimary>
          <PanelSecondary disabled={busy || !downloaded} onClick={onFinish}>
            {busy ? t.finishing : t.finish}
          </PanelSecondary>
        </div>
      </div>
    );
  }

  return (
    <form aria-label={t.title} onSubmit={onPrepare}>
      <PanelNote>
        <span className="block font-semibold text-white">{t.title}</span>
        {t.note(MIN_PASSPHRASE_LENGTH)}
      </PanelNote>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <PanelInput
          label={t.passphrase}
          type="password"
          autoComplete="new-password"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
        />
        <PanelInput
          label={t.confirm}
          type="password"
          autoComplete="new-password"
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
        />
      </div>
      {passphrase && problem ? <div className="mt-2 text-xs text-orange">{problem}</div> : null}
      <PanelPrimary type="submit" className="mt-5" disabled={busy || !!problem}>
        {busy ? t.generating : t.submit}
      </PanelPrimary>
    </form>
  );
}
